<script setup lang="ts">
import { UIBuilderSpace } from "@gohighlevel/ghl-ui";
import { computed } from "vue";

interface Space {
  padding: { top: number; right: number; bottom: number; left: number };
  margin: {
    top: number | "auto";
    right: number | "auto";
    bottom: number | "auto";
    left: number | "auto";
  };
}

const props = defineProps({
  label: {
    type: String,
    default: "Margin & Padding",
  },
  space: {
    type: Object as () => Space,
    default: () => ({
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
    }),
  },
  paddingUnitsConfig: {
    type: Array as () => any[],
    default: () => ["px"],
  },
  marginUnitsConfig: {
    type: Array as () => any[],
    default: () => ["auto"],
  },
});

const emit = defineEmits<{
  (
    e: "updateSpace",
    type: "padding" | "margin",
    side: "top" | "right" | "bottom" | "left",
    value: number | string
  ): void;
}>();

const updatedValue = (value: any) => {
  const type: "padding" | "margin" = value?.changed?.padding ? "padding" : "margin";
  const side = Object.keys(value?.changed?.[type] || { top: 0 })[0] as
    | "top"
    | "right"
    | "bottom"
    | "left";
  const numericValue = value?.numericValue;
  const newValue = numericValue === "auto" ? "auto" : Number.parseFloat(numericValue ?? 0);
  emit("updateSpace", type, side, newValue);
};

const padding = computed(() => ({
  top: Number(((props.space as any)?.padding?.top ?? 0) as number),
  right: Number(((props.space as any)?.padding?.right ?? 0) as number),
  bottom: Number(((props.space as any)?.padding?.bottom ?? 0) as number),
  left: Number(((props.space as any)?.padding?.left ?? 0) as number),
}));

const paddingPixel = computed(() => ({
  top: `${padding.value.top}px`,
  right: `${padding.value.right}px`,
  bottom: `${padding.value.bottom}px`,
  left: `${padding.value.left}px`,
}));

const margin = computed(() => ({
  top: ((props.space as any)?.margin?.top ?? 0) as number | "auto",
  right: ((props.space as any)?.margin?.right ?? 0) as number | "auto",
  bottom: ((props.space as any)?.margin?.bottom ?? 0) as number | "auto",
  left: ((props.space as any)?.margin?.left ?? 0) as number | "auto",
}));

const marginPixel = computed(() => ({
  top: margin.value.top === "auto" ? "auto" : `${Number(margin.value.top || 0)}px`,
  right: margin.value.right === "auto" ? "auto" : `${Number(margin.value.right || 0)}px`,
  bottom: margin.value.bottom === "auto" ? "auto" : `${Number(margin.value.bottom || 0)}px`,
  left: margin.value.left === "auto" ? "auto" : `${Number(margin.value.left || 0)}px`,
}));
</script>

<template>
  <div class="flex flex-col items-start gap-2 self-stretch">
    <span class="mb-1 text-[12px] font-medium">{{ label }}</span>
    <div class="flex self-stretch">
      <UIBuilderSpace
        class="w-full max-w-[360px]"
        :margin="marginPixel"
        :padding="paddingPixel"
        :padding-units-config="paddingUnitsConfig"
        :margin-units-config="marginUnitsConfig"
        @update:value="updatedValue"
      />
    </div>
  </div>
</template>

<style scoped></style>
